import React, { useState, useEffect } from 'react';
import {
  PlusCircleOutlined,
  MinusCircleOutlined,
  SaveOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import {
  Row,
  Col,
  Form,
  InputNumber,
  Button,
  Modal,
  notification,
  Card,
  TimePicker,
  Select,
  Divider,
  Typography,
  Space,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { PAGE_URLS } from 'helpers/endpoints';
import { setLoading } from 'store/requests/routing';
import { STATUS } from '../../../../helpers/constants';
import {
  getV2Regions,
  getV2Stores,
  getV2Zones,
} from '../../../../store/requests/definitions';

const { Option } = Select;
const { Text } = Typography;

const AllSlotUpdate = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [regions, setRegions] = useState([]);
  const [stores, setStores] = useState([]);
  const [zones, setZones] = useState([]);
  const [isLoadingZones, setIsLoadingZones] = useState(false);
  const watchTimeSlots = Form.useWatch('timeSlots', form) || [];
  const [isAddDisabled, setIsAddDisabled] = useState(true);

  useEffect(() => {
    getV2Regions(`?size=20000`).then((res) => setRegions(res?.content || []));
  }, []);

  useEffect(() => {
    const hasValidTimeSlots =
      watchTimeSlots.length > 0 &&
      watchTimeSlots.every(
        (slot) =>
          slot?.timeRange?.length === 2 &&
          slot?.deliveryFees?.every(
            (fee) =>
              fee.minCartAmount != null &&
              fee.maxCartAmount != null &&
              fee.deliveryFee != null
          )
      );
    setIsAddDisabled(watchTimeSlots.length > 0 && !hasValidTimeSlots);
    setIsButtonDisabled(!hasValidTimeSlots);
  }, [watchTimeSlots]);

  const handleRegionChange = (selected) => {
    form.setFieldsValue({ selectedStores: [], selectedZones: [] });
    setStores([]);
    setZones([]);
    if (selected.length) {
      getV2Stores(`?filter=region.id=in=(${selected})&size=20000`).then((res) =>
        setStores(res?.content || [])
      );
    }
  };

  const handleStoreChange = (selected) => {
    setIsLoadingZones(true);
    form.setFieldsValue({ selectedZones: [] });
    setZones([]);
    if (selected.length) {
      getV2Zones(`?filter=store.id=in=(${selected})&size=20000`)
        .then((res) => {
          setZones(res?.content || []);
        })
        .finally(() => setIsLoadingZones(false));
    } else {
      setIsLoadingZones(false);
    }
  };

  const addTimeSlot = () => {
    const currentTimeSlots = form.getFieldValue('timeSlots') || [];
    form.setFieldsValue({
      timeSlots: [
        ...currentTimeSlots,
        {
          timeRange: null,
          deliveryFees: [
            { minCartAmount: 0.0, maxCartAmount: null, deliveryFee: null },
          ],
        },
      ],
    });
  };

  const removeTimeSlot = (index) => {
    const currentTimeSlots = form.getFieldValue('timeSlots') || [];
    form.setFieldsValue({
      timeSlots: currentTimeSlots.filter((_, i) => i !== index),
    });
  };

  const handleAddDeliveryFee = (timeSlotIndex, fields, add) => {
    if (fields.length) {
      const lastField = fields[fields.length - 1];
      form
        .validateFields([
          [
            'timeSlots',
            timeSlotIndex,
            'deliveryFees',
            lastField.name,
            'minCartAmount',
          ],
          [
            'timeSlots',
            timeSlotIndex,
            'deliveryFees',
            lastField.name,
            'maxCartAmount',
          ],
          [
            'timeSlots',
            timeSlotIndex,
            'deliveryFees',
            lastField.name,
            'deliveryFee',
          ],
        ])
        .then(() => {
          const lastItem = form.getFieldValue([
            'timeSlots',
            timeSlotIndex,
            'deliveryFees',
            lastField.name,
          ]);
          add({
            minCartAmount: lastItem
              ? Math.round((lastItem.maxCartAmount + 0.01) * 100) / 100
              : 0.0,
            maxCartAmount: null,
            deliveryFee: null,
          });
        })
        .catch(() => {});
    } else {
      add({ minCartAmount: 0.0, maxCartAmount: null, deliveryFee: null });
    }
  };

  const notificationSave = (type, descriptionKey) => {
    notification[type]({
      message: t('processCompleted'),
      description: t(descriptionKey),
    });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      Modal.confirm({
        title: t('takeFewMinutes'),
        content: t('allSlotUpdateWarning'),
        okText: t('yes'),
        cancelText: t('no'),
        centered: true,
        width: 600,
        onOk: async () => {
          try {
            const payload = {
              regions: values.selectedRegions || [],
              stores: values.selectedStores || [],
              zones: values.selectedZones || [],
              timeSlots: values.timeSlots || [],
            };
            console.log('Payload:', payload);
            await new Promise((resolve) => setTimeout(resolve, 2000));
            notificationSave(
              STATUS.SUCCESS,
              'slotTemplatesSuccessfullyUpdated'
            );
            setStores([]);
            setZones([]);
            form.resetFields();
          } catch (error) {
            notification.error({
              message: t('error'),
              description: t('updateFailed'),
            });
          } finally {
            setLoading(false);
            Modal.destroyAll();
          }
        },
        onCancel: () => setLoading(false),
      });
    } catch (error) {
      console.log('Validation failed:', error);
    }
  };

  const selectFilter = (input, option) =>
    (option?.children ?? '')
      .toLocaleLowerCase('tr-TR')
      .includes(input.toLocaleLowerCase('tr-TR'));

  return (
    <div style={{ position: 'relative' }}>
      <Form
        name="slotUpdateForm"
        form={form}
        layout="vertical"
        initialValues={{
          timeSlots: [
            {
              timeRange: null,
              deliveryFees: [
                { minCartAmount: 0.0, maxCartAmount: null, deliveryFee: null },
              ],
            },
          ],
        }}
      >
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                label={t('region')}
                name="selectedRegions"
                rules={[{ required: true, message: t('required') }]}
              >
                <Select
                  mode="multiple"
                  placeholder={t('selectRegion')}
                  showSearch
                  optionFilterProp="children"
                  onChange={handleRegionChange}
                  maxTagCount={2}
                  filterOption={selectFilter}
                >
                  {regions.map((region) => (
                    <Option key={region.id} value={region.id}>
                      {region.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={t('store')} name="selectedStores">
                <Select
                  mode="multiple"
                  placeholder={t('selectStore')}
                  showSearch
                  optionFilterProp="children"
                  onChange={handleStoreChange}
                  disabled={!stores.length}
                  maxTagCount={2}
                  filterOption={selectFilter}
                >
                  {stores.map((store) => (
                    <Option
                      key={store.id}
                      value={store.id}
                    >{`${store.code}-${store.name}`}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={t('zone')} name="selectedZones">
                <Select
                  mode="multiple"
                  placeholder={t('selectZone')}
                  showSearch
                  loading={isLoadingZones}
                  disabled={!zones.length}
                  maxTagCount={2}
                  optionFilterProp="children"
                  filterOption={selectFilter}
                >
                  {zones.map((zone) => (
                    <Option
                      key={zone.id}
                      value={zone.id}
                    >{`${zone.code}-${zone.name}`}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          extra={
            <Button
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={addTimeSlot}
              disabled={isAddDisabled}
            >
              {t('addSlot')}
            </Button>
          }
          style={{ marginBottom: 24 }}
        >
          <Form.List name="timeSlots">
            {(timeSlotFields) => (
              <>
                {timeSlotFields.map((timeSlotField, timeSlotIndex) => (
                  <Card
                    key={timeSlotField.key}
                    size="small"
                    style={{ marginBottom: 24 }}
                    title={
                      <Space style={{ padding: '8px 0' }}>
                        <ClockCircleOutlined />
                        <span>{t('Slot')}:</span>
                        {form.getFieldValue([
                          'timeSlots',
                          timeSlotIndex,
                          'timeRange',
                        ]) && (
                          <span style={{ color: 'green' }}>
                            {form
                              .getFieldValue([
                                'timeSlots',
                                timeSlotIndex,
                                'timeRange',
                              ])
                              .map((time) => time.format('HH:mm'))
                              .join(' - ')}
                          </span>
                        )}
                      </Space>
                    }
                    extra={
                      timeSlotFields.length > 1 && (
                        <Button
                          type="text"
                          danger
                          size="medium"
                          icon={<DeleteOutlined />}
                          onClick={() => removeTimeSlot(timeSlotIndex)}
                        />
                      )
                    }
                  >
                    <Divider orientation="left">
                      <Text>{t('slots')}</Text>
                    </Divider>
                    <Row gutter={24}>
                      <Col span={12}>
                        <Form.Item
                          {...timeSlotField}
                          name={[timeSlotField.name, 'timeRange']}
                          rules={[{ required: true, message: t('required') }]}
                        >
                          <TimePicker.RangePicker
                            format="HH:mm"
                            placeholder={[t('startTime'), t('endTime')]}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Divider orientation="left">
                      <Text>{t('flatDeliveryFee')}</Text>
                    </Divider>
                    <Form.List name={[timeSlotField.name, 'deliveryFees']}>
                      {(
                        deliveryFeeFields,
                        { add: addDeliveryFee, remove: removeDeliveryFee }
                      ) => (
                        <>
                          {deliveryFeeFields.map(
                            (deliveryFeeField, deliveryFeeIndex) => (
                              <Row
                                key={deliveryFeeField.key}
                                gutter={20}
                                align="end"
                                style={{ marginBottom: 16 }}
                              >
                                <Col span={8}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                    }}
                                  >
                                    <label style={{ marginBottom: '8px' }}>
                                      {t('minimumCartAmount')}
                                    </label>
                                    <Form.Item
                                      {...deliveryFeeField}
                                      name={[
                                        deliveryFeeField.name,
                                        'minCartAmount',
                                      ]}
                                      rules={[
                                        {
                                          required: true,
                                          message: t('alert.fillField'),
                                        },
                                      ]}
                                      style={{ margin: 0 }}
                                    >
                                      <InputNumber
                                        min={0}
                                        step={0.1}
                                        maxLength={11}
                                        style={{ width: '100%' }}
                                        disabled={deliveryFeeIndex > 0}
                                        formatter={(value) =>
                                          value
                                            ? value.replace(
                                                /(\.\d{2})\d+/,
                                                '$1'
                                              )
                                            : value
                                        }
                                        parser={(value) =>
                                          value.replace(/[^\d.]/g, '')
                                        }
                                      />
                                    </Form.Item>
                                  </div>
                                </Col>
                                <Col span={8}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                    }}
                                  >
                                    <label style={{ marginBottom: '8px' }}>
                                      {t('maximumCartAmount')}
                                    </label>
                                    <Form.Item
                                      {...deliveryFeeField}
                                      name={[
                                        deliveryFeeField.name,
                                        'maxCartAmount',
                                      ]}
                                      rules={[
                                        {
                                          required: true,
                                          message: t('alert.fillField'),
                                        },
                                      ]}
                                      style={{ margin: 0 }}
                                    >
                                      <InputNumber
                                        min={0}
                                        step={0.1}
                                        maxLength={11}
                                        style={{ width: '100%' }}
                                        formatter={(value) =>
                                          value
                                            ? value.replace(
                                                /(\.\d{2})\d+/,
                                                '$1'
                                              )
                                            : value
                                        }
                                        parser={(value) =>
                                          value.replace(/[^\d.]/g, '')
                                        }
                                      />
                                    </Form.Item>
                                  </div>
                                </Col>
                                <Col span={8}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                    }}
                                  >
                                    <label style={{ marginBottom: '8px' }}>
                                      {t('deliveryFee')}
                                    </label>
                                    <Form.Item
                                      {...deliveryFeeField}
                                      name={[
                                        deliveryFeeField.name,
                                        'deliveryFee',
                                      ]}
                                      rules={[
                                        {
                                          required: true,
                                          message: t('alert.fillField'),
                                        },
                                      ]}
                                      style={{ margin: 0 }}
                                    >
                                      <InputNumber
                                        min={0}
                                        step={0.1}
                                        maxLength={11}
                                        style={{ width: '100%' }}
                                        formatter={(value) =>
                                          value
                                            ? value.replace(
                                                /(\.\d{2})\d+/,
                                                '$1'
                                              )
                                            : value
                                        }
                                        parser={(value) =>
                                          value.replace(/[^\d.]/g, '')
                                        }
                                      />
                                    </Form.Item>
                                  </div>
                                </Col>
                              </Row>
                            )
                          )}
                          <Row>
                            <Col span={24}>
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  marginTop: '10px',
                                  marginBottom: '10px',
                                  gap: '10px',
                                  alignItems: 'center',
                                }}
                              >
                                {deliveryFeeFields.length > 1 && (
                                  <MinusCircleOutlined
                                    style={{
                                      fontSize: '24px',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() =>
                                      deliveryFeeFields.length > 1 &&
                                      removeDeliveryFee(
                                        deliveryFeeFields.length - 1
                                      )
                                    }
                                  />
                                )}
                                <PlusCircleOutlined
                                  style={{
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() =>
                                    handleAddDeliveryFee(
                                      timeSlotIndex,
                                      deliveryFeeFields,
                                      addDeliveryFee
                                    )
                                  }
                                />
                              </div>
                            </Col>
                          </Row>
                        </>
                      )}
                    </Form.List>
                  </Card>
                ))}
              </>
            )}
          </Form.List>
        </Card>

        {isCrudEnabled && (
          <Row justify="end">
            <Col>
              <Button
                type="primary"
                size="large"
                icon={<SaveOutlined />}
                onClick={handleSave}
                disabled={isButtonDisabled}
              >
                {t('save')}
              </Button>
            </Col>
          </Row>
        )}
      </Form>
    </div>
  );
};

export default AllSlotUpdate;
